import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
    getExternalTables,
    deleteExternalTable,
    listSchemas,
    listTables,
    listColumns,
    createDimensionTable,
    createFactTable,
    generateAllIDs,
} from './externalTablesApi'

/**
 * Hook for managing external tables - CRUD operations and schema browsing
 *
 * @param {Object} connectionDetails - Connection credentials for schema browsing (optional)
 * @param {string} connectionDetails.jdbcUrl - JDBC connection URL
 * @param {string} connectionDetails.username - Database username
 * @param {string} connectionDetails.password - Database password
 * @param {string} connectionDetails.driverClass - JDBC driver class
 * @param {string} selectedSchema - Currently selected schema (for tables query)
 */
export const useExternalTables = (connectionDetails, selectedSchema) => {
    const queryClient = useQueryClient()

    // ==========================================================================
    // CRUD Operations for External Tables
    // ==========================================================================

    // Query: Get all external tables
    const { data: externalTables, isLoading: loadingExternalTables } = useQuery({
        queryKey: ['externalTables'],
        queryFn: getExternalTables,
    })

    // Mutation: Delete external table
    const { mutate: deleteTable, isPending: isDeletingTable } = useMutation({
        mutationFn: deleteExternalTable,
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({ queryKey: ['externalTables'] }).then(() => {
                if (variables?.onSuccess) variables.onSuccess(response)
            })
        },
        onError: (error, variables) => {
            if (variables?.onError) variables.onError(error)
        },
    })

    // ==========================================================================
    // Schema Browsing Queries
    // ==========================================================================

    // Query: List schemas (auto-fetches when connectionDetails is available)
    const { data: schemasData, isLoading: isLoadingSchemas } = useQuery({
        queryKey: ['externalSchemas', connectionDetails?.jdbcUrl],
        queryFn: () => listSchemas(connectionDetails),
        enabled: Boolean(connectionDetails?.jdbcUrl),
    })

    // Query: List tables (auto-fetches when schema is selected)
    const { data: tablesData, isLoading: isLoadingTables } = useQuery({
        queryKey: ['externalSchemaTables', connectionDetails?.jdbcUrl, selectedSchema],
        queryFn: () => listTables(connectionDetails, selectedSchema),
        enabled: Boolean(connectionDetails?.jdbcUrl && selectedSchema),
    })

    // ==========================================================================
    // On-demand fetching for columns (used when selecting tables in wizard)
    // This is a mutation because we need to fetch columns for arbitrary tables
    // and get the result directly (not cache it as "the selected table's columns")
    // ==========================================================================

    const { mutateAsync: fetchColumns, isPending: isLoadingColumns } = useMutation({
        mutationFn: ({ schema, tableName }) => listColumns(connectionDetails, schema, tableName),
    })

    // ==========================================================================
    // Star Schema Creation Mutations
    // Used by the wizard to create dimensions first, then fact table
    // ==========================================================================

    // Mutation: Generate all IDs for dimensions + fact table
    const { mutateAsync: generateIDs } = useMutation({
        mutationFn: ({ dimensionCount, dimensions, factTableName }) =>
            generateAllIDs(dimensionCount, dimensions, factTableName),
    })

    // Mutation: Create a dimension table (ReportInbox + RST)
    const { mutateAsync: createDimension } = useMutation({
        mutationFn: ({ dimension, inboxID, wizardData }) =>
            createDimensionTable(dimension, inboxID, wizardData),
    })

    // Mutation: Create a fact table (ReportInbox + RST with joinRelationships)
    const { mutateAsync: createFact } = useMutation({
        mutationFn: ({ factTable, inboxID, wizardData, dimensionRSTMap }) =>
            createFactTable(factTable, inboxID, wizardData, dimensionRSTMap),
        onSuccess: () => {
            // Invalidate external tables list after fact table is created
            queryClient.invalidateQueries({ queryKey: ['externalTables'] })
        },
    })

    return {
        // External Tables CRUD
        externalTables: externalTables || [],
        loadingExternalTables,
        deleteTable,
        isDeletingTable,

        // Schema Browsing - Cached Queries
        schemas: schemasData?.schemas || [],
        isLoadingSchemas,
        tables: tablesData?.tables || [],
        isLoadingTables,

        // On-demand fetching (returns promise with data)
        fetchColumns,
        isLoadingColumns,

        // Star Schema Creation
        generateIDs,
        createDimension,
        createFact,
    }
}
